
import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim() || formData.name.length < 2) {
      newErrors.name = 'Le nom doit contenir au moins 2 caractères';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Veuillez entrer une adresse email valide';
    }

    const phoneRegex = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;
    if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Veuillez entrer un numéro de téléphone valide';
    }

    if (!formData.service) {
      newErrors.service = 'Veuillez sélectionner un service';
    }

    if (!formData.message.trim() || formData.message.length < 10) {
      newErrors.message = 'Le message doit contenir au moins 10 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formDataToSend = new FormData(form);
    const formBody = new URLSearchParams();
    
    formDataToSend.forEach((value, key) => {
      formBody.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d4unoffsdr6u9jrifio0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (!response.ok) throw new Error('Erreur lors de l\'envoi');

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: '',
      });
      setErrors({});

      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  return (
    <section id="contact" className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-12 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
            Demandez Votre Devis Gratuit
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Remplissez le formulaire ci-dessous et recevez une réponse personnalisée sous 24h
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 mt-5 sm:mt-6 px-4">
            <div className="flex items-center gap-2 text-gray-600">
              <i className="ri-shield-check-line text-teal-600 text-lg sm:text-xl"></i>
              <span className="text-xs sm:text-sm font-medium">Réponse rapide</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <i className="ri-lock-line text-teal-600 text-lg sm:text-xl"></i>
              <span className="text-xs sm:text-sm font-medium">Données sécurisées</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <i className="ri-gift-line text-teal-600 text-lg sm:text-xl"></i>
              <span className="text-xs sm:text-sm font-medium">Devis gratuit</span>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6 bg-gray-50 p-5 sm:p-8 rounded-2xl shadow-lg" data-readdy-form id="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Nom complet <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-user-line text-gray-400 text-base"></i>
                  </div>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className={`w-full pl-11 pr-4 py-3.5 sm:py-3 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base sm:text-sm bg-white transition-all duration-200`}
                    placeholder="Jean Dupont"
                  />
                </div>
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    {errors.name}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-mail-line text-gray-400 text-base"></i>
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={`w-full pl-11 pr-4 py-3.5 sm:py-3 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base sm:text-sm bg-white transition-all duration-200`}
                    placeholder="jean.dupont@email.com"
                  />
                </div>
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    {errors.email}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Téléphone <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-phone-line text-gray-400 text-base"></i>
                  </div>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className={`w-full pl-11 pr-4 py-3.5 sm:py-3 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base sm:text-sm bg-white transition-all duration-200`}
                    placeholder="06 12 34 56 78"
                  />
                </div>
                {errors.phone && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    {errors.phone}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                  Type de service <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-tools-line text-gray-400 text-base"></i>
                  </div>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className={`w-full pl-11 pr-10 py-3.5 sm:py-3 border ${errors.service ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base sm:text-sm bg-white transition-all duration-200 appearance-none cursor-pointer`}
                  >
                    <option value="">Sélectionnez un service</option>
                    <option value="Réparation fenêtre PVC">Réparation fenêtre PVC</option>
                    <option value="Réparation fenêtre aluminium">Réparation fenêtre aluminium</option>
                    <option value="Réparation fenêtre bois">Réparation fenêtre bois</option>
                    <option value="Réparation baie vitrée">Réparation baie vitrée</option>
                    <option value="Remplacement quincaillerie">Remplacement quincaillerie</option>
                    <option value="Autre">Autre</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                    <i className="ri-arrow-down-s-line text-gray-400"></i>
                  </div>
                </div>
                {errors.service && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    {errors.service}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                Décrivez votre projet <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                maxLength={500}
                className={`w-full px-4 py-3.5 sm:py-3 border ${errors.message ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none text-base sm:text-sm bg-white transition-all duration-200`}
                placeholder="Décrivez le problème rencontré avec vos fenêtres (type de dégât, urgence, dimensions approximatives...)"
              />
              <div className="flex justify-between items-center mt-2">
                {errors.message ? (
                  <p className="text-red-500 text-xs flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    {errors.message}
                  </p>
                ) : (
                  <p className="text-xs sm:text-sm text-gray-500">
                    Minimum 10 caractères
                  </p>
                )}
                <p className={`text-xs sm:text-sm ${formData.message.length > 450 ? 'text-orange-500 font-medium' : 'text-gray-500'}`}>
                  {formData.message.length}/500
                </p>
              </div>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border-2 border-green-200 text-green-700 px-4 sm:px-6 py-4 rounded-xl animate-[fadeIn_0.3s_ease-in]">
                <div className="flex items-start sm:items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl sm:text-3xl flex-shrink-0 mt-0.5 sm:mt-0"></i>
                  <div>
                    <p className="font-semibold text-base sm:text-lg">Demande envoyée avec succès !</p>
                    <p className="text-xs sm:text-sm text-green-600 mt-1">Nous vous répondrons dans les 24h. Vérifiez vos emails.</p>
                  </div>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 sm:px-6 py-4 rounded-xl animate-[fadeIn_0.3s_ease-in]">
                <div className="flex items-start sm:items-center gap-3">
                  <i className="ri-error-warning-fill text-2xl sm:text-3xl flex-shrink-0 mt-0.5 sm:mt-0"></i>
                  <div>
                    <p className="font-semibold text-base sm:text-lg">Une erreur est survenue</p>
                    <p className="text-xs sm:text-sm text-red-600 mt-1">Veuillez réessayer ou nous contacter au 09 80 80 36 84</p>
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-teal-600 to-teal-700 text-white py-4 sm:py-4.5 rounded-xl font-bold text-base sm:text-lg hover:from-teal-700 hover:to-teal-800 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-3">
                  <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                  Envoi en cours...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-send-plane-fill text-lg sm:text-xl"></i>
                  Envoyer ma demande de devis
                </span>
              )}
            </button>

            <p className="text-xs text-gray-500 text-center px-2">
              En soumettant ce formulaire, vous acceptez d'être contacté par nos services. 
              Vos données sont protégées et ne seront jamais partagées.
            </p>
          </form>

          {/* Contact Cards - OPTIMIZED FOR MOBILE */}
          <div className="mt-8 sm:mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <a 
              href="tel:0980803684"
              className="text-center p-5 sm:p-6 bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:scale-105 active:scale-95 cursor-pointer group"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:bg-teal-700 transition-colors duration-300">
                <i className="ri-phone-line text-xl sm:text-2xl text-white"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Appelez-nous</h3>
              <p className="text-teal-600 hover:text-teal-700 font-bold text-base sm:text-lg">
                09 80 80 36 84
              </p>
              <p className="text-xs text-gray-600 mt-1">Lun-Ven 8h-19h</p>
            </a>

            <a 
              href="mailto:info@fenetreslyon.fr"
              className="text-center p-5 sm:p-6 bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:scale-105 active:scale-95 cursor-pointer group"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:bg-teal-700 transition-colors duration-300">
                <i className="ri-mail-line text-xl sm:text-2xl text-white"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Écrivez-nous</h3>
              <p className="text-teal-600 hover:text-teal-700 font-medium break-all text-xs sm:text-sm">
                info@fenetreslyon.fr
              </p>
              <p className="text-xs text-gray-600 mt-1">Réponse sous 24h</p>
            </a>

            <div className="text-center p-5 sm:p-6 bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:scale-105 group">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:bg-teal-700 transition-colors duration-300">
                <i className="ri-map-pin-line text-xl sm:text-2xl text-white"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Zone d'intervention</h3>
              <p className="text-gray-700 font-medium text-sm">Lyon et agglomération</p>
              <p className="text-xs text-gray-600 mt-1">Déplacement gratuit</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
